import { useContext, useState } from "react";
import { Box, Switch, Divider } from "~/components/UI";
import ConfirmDialog from "~/components/BaseConfirmDialog";
import Table, { TableProps } from "~/components/UI/Table/Table";
import ServicesContext from "./servicesContext";
import ServiceDetails from "./ServiceDetails";
import ServiceOptions from "./ServiceOptions";
import IncidentsIcon from "~/components/Incidents/IncidentsIcon";
import { useTranslation } from "react-i18next";
import { i18nTableColumn } from "~/packages/i18n";
import { PERMISSION } from "~/api/permissions/enums";
import usePermissions from "~/hook/usePermissions";

const defaultColumns = [
  {
    key: "serviceId",
    sort: true,
    title: "ID",
    cellOptions: {
      width: 200,
    },
  },
  {
    key: "serviceName",
    sort: true,
    i18nKey: "serviceName",
  },
  {
    key: "enabled",
    sort: true,
    i18nKey: "enabled",
    cellOptions: {
      width: 100,
    },
  },
  {
    key: "siteName",
    sort: true,
    i18nKey: "siteName",
    cellOptions: {
      width: 350,
    },
  },
  {
    key: "token",
    sort: true,
    copy: true,
    i18nKey: "token",
    cellOptions: {
      width: 200,
    },
  },
  {
    key: "incidents",
    cellOptions: {
      align: "center",
      padding: "none",
      width: 50,
    },
    render: (incidents: IncidentsApi.IncidentsCount) => (
      <IncidentsIcon incidents={incidents} />
    ),
  },
  {
    key: "options",
    cellOptions: {
      align: "right",
      padding: "none",
      width: 70,
    },
  },
];

/**
 * @memberof Services
 * @component
 * @desc Render table of services.
 * @property {Api.ServiceDto[]} dataSource - Services data
 * @requires ServicesContext
 */

const ServicesTable = (props: Omit<TableProps<Api.ServiceDto>, "columns">) => {
  const { onUpdate, onSelect, onSort } = useContext(ServicesContext);
  const { hasAll } = usePermissions();
  const { t } = useTranslation();
  const [rowSelection] = useState(
    hasAll(PERMISSION.DELETE_SERVICE)
      ? {
          hideSelectAll: false,
          onChange: onSelect,
        }
      : undefined
  );

  const columns = defaultColumns.map((c) => {
    const column = i18nTableColumn(c, t);
    switch (column.key) {
      case "enabled":
        return {
          ...column,
          render: (enabled: boolean, service: Api.ServiceDto) => (
            <ConfirmDialog
              placement="top"
              disabled={!hasAll(PERMISSION.EDIT_SERVICE)}
              onConfirm={() =>
                onUpdate &&
                onUpdate(service.serviceId, service.siteId, {
                  enabled: !enabled,
                })
              }
            >
              <Switch
                checked={enabled}
                disabled={!hasAll(PERMISSION.EDIT_SERVICE)}
              />
            </ConfirmDialog>
          ),
        };
      case "options":
        return {
          ...column,
          render: (_value: unknown, row: Api.ServiceDto) => (
            <ServiceOptions service={row} />
          ),
        };
      default:
        return column;
    }
  });

  return (
    <Box bgcolor="background.paper">
      <Table
        {...props}
        columns={columns}
        stickyHeader
        onRequestSort={onSort}
        rowKey="id"
        rowSelection={rowSelection}
        expandable={{
          highlightRow: true,
          expandedRowRender: (row: Api.ServiceDto) => (
            <ServiceDetails service={row} />
          ),
        }}
      />
      <Divider />
    </Box>
  );
};

export default ServicesTable;
